/**
 * CategoryManager - Manage menu categories with drag-to-reorder and subcategories
 */
import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, GripVertical, X, Check, ChevronRight, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/utils/apiClient';

export default function CategoryManager({ onCategoriesChange }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', parent_id: null });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    try {
      // Admin needs to see ALL categories including hidden ones
      const res = await api.get('/menu/categories?include_hidden=true');
      setCategories(res.data);
      onCategoriesChange?.(res.data);
    } catch (err) {
      console.error('Failed to fetch categories');
    }
    setLoading(false);
  };

  const addCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      const payload = { 
        name: newCategory.name.trim(),
        parent_id: newCategory.parent_id || null
      };
      
      await api.post('/menu/categories', payload);
      toast.success('Category added!');
      setNewCategory({ name: '', parent_id: null });
      setShowAddDialog(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add category');
    }
  };

  const updateCategory = async () => {
    if (!editingCategory?.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      await api.put(`/menu/categories/${editingCategory.id}`, { 
        name: editingCategory.name,
        parent_id: editingCategory.parent_id || null
      });
      toast.success('Category updated!');
      setEditingCategory(null);
      fetchCategories();
    } catch (err) {
      toast.error('Failed to update category');
    }
  };

  const deleteCategory = async (catId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;

    try {
      await api.delete(`/menu/categories/${catId}`);
      toast.success('Category deleted!');
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = draggedIndex;
    
    if (dragIndex === null || dragIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder categories
    const newCategories = [...categories];
    const [draggedItem] = newCategories.splice(dragIndex, 1);
    newCategories.splice(dropIndex, 0, draggedItem);
    
    // Update order values
    const reordered = newCategories.map((cat, i) => ({ ...cat, order: i }));
    setCategories(reordered);
    onCategoriesChange?.(reordered);

    // Save to backend
    try {
      await api.put('/menu/categories/reorder', { categories: reordered.map(c => ({ id: c.id })) });
      toast.success('Categories reordered!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save order');
      fetchCategories(); // Revert on error
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const toggleCategoryActive = async (cat) => {
    try {
      await api.put(`/menu/categories/${cat.id}`, { is_active: !cat.is_active });
      fetchCategories();
      toast.success(cat.is_active ? 'Category hidden' : 'Category visible');
    } catch (err) {
      toast.error('Failed to update category');
    }
  };

  // Get parent categories (for subcategory selection)
  const parentCategories = categories.filter(c => !c.parent_id);
  
  // Get subcategories for a parent
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl p-6 shadow-sm border border-border mb-6" data-testid="category-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-xl text-foreground">Menu Categories</h3>
          <p className="text-sm text-muted-foreground">
            Drag to reorder • Click edit to rename • Add subcategories under main categories
          </p>
        </div>
        <Button 
          onClick={() => setShowAddDialog(true)}
          className="rounded-full bg-primary"
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Category
        </Button>
      </div>

      {/* Categories List */}
      <div className="space-y-1 border rounded-xl overflow-hidden">
        {categories.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <p>No categories yet. Add your first category!</p>
          </div>
        ) : (
          categories.map((cat, index) => (
            <div 
              key={cat.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={`
                flex items-center gap-3 p-3 bg-background border-b last:border-b-0 cursor-move transition-all
                ${dragOverIndex === index ? 'bg-primary/10 border-primary' : ''}
                ${draggedIndex === index ? 'opacity-50' : ''}
                ${cat.parent_id ? 'pl-10 bg-muted/30' : ''}
                ${!cat.is_active ? 'opacity-60' : ''}
              `}
              data-testid={`category-row-${cat.id}`}
            >
              {/* Drag Handle */}
              <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                <GripVertical className="w-5 h-5" />
              </div>

              {/* Subcategory Indicator */}
              {cat.parent_id && (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}

              {/* Category Info */}
              {editingCategory?.id === cat.id ? (
                <div className="flex-1 flex items-center gap-2">
                  <Input
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                    className="h-8 max-w-[200px]"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && updateCategory()}
                  />
                  <Select 
                    value={editingCategory.parent_id || "none"}
                    onValueChange={(value) => setEditingCategory({ 
                      ...editingCategory, 
                      parent_id: value === "none" ? null : value 
                    })}
                  >
                    <SelectTrigger className="h-8 w-[150px]">
                      <SelectValue placeholder="Parent..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Parent (Main)</SelectItem>
                      {parentCategories.filter(p => p.id !== cat.id).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => setEditingCategory(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={updateCategory} className="bg-primary">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex-1">
                    <span className={`font-medium ${!cat.is_active ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {cat.name}
                    </span>
                    {cat.parent_id && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        (under {categories.find(c => c.id === cat.parent_id)?.name || 'unknown'})
                      </span>
                    )}
                  </div>

                  {/* Category ID Badge */}
                  <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded-full font-mono">
                    {cat.id}
                  </span>

                  {/* Active Toggle */}
                  <button
                    onClick={() => toggleCategoryActive(cat)}
                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                      cat.is_active 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    {cat.is_active ? 'Visible' : 'Hidden'}
                  </button>

                  {/* Add Subcategory Button */}
                  {!cat.parent_id && (
                    <button
                      onClick={() => {
                        setNewCategory({ name: '', parent_id: cat.id });
                        setShowAddDialog(true);
                      }}
                      className="p-2 hover:bg-primary/10 rounded-full text-primary"
                      title="Add subcategory"
                    >
                      <FolderPlus className="w-4 h-4" />
                    </button>
                  )}

                  {/* Edit Button */}
                  <button
                    onClick={() => setEditingCategory({ ...cat })}
                    className="p-2 hover:bg-blue-100 rounded-full text-blue-600"
                    data-testid={`edit-category-${cat.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => deleteCategory(cat.id)}
                    className="p-2 hover:bg-red-100 rounded-full text-red-600"
                    data-testid={`delete-category-${cat.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Quick Stats */}
      <div className="mt-4 flex gap-4 text-sm text-muted-foreground">
        <span>{categories.length} categories total</span>
        <span>{parentCategories.length} main categories</span>
        <span>{categories.filter(c => c.parent_id).length} subcategories</span>
      </div>

      {/* Add Category Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {newCategory.parent_id 
                ? `Add Subcategory under "${categories.find(c => c.id === newCategory.parent_id)?.name}"`
                : 'Add New Category'
              }
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Salads, Desserts, Hot Drinks"
                onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                autoFocus
                data-testid="new-category-name-input"
              />
            </div>

            {!newCategory.parent_id && (
              <div className="space-y-2">
                <Label>Parent Category (optional)</Label>
                <Select 
                  value={newCategory.parent_id || "none"}
                  onValueChange={(value) => setNewCategory(prev => ({ 
                    ...prev, 
                    parent_id: value === "none" ? null : value 
                  }))}
                >
                  <SelectTrigger data-testid="parent-category-select">
                    <SelectValue placeholder="Select parent category..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Main Category)</SelectItem>
                    {parentCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Create a subcategory under an existing category, or leave empty for a main category.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => {
                setShowAddDialog(false);
                setNewCategory({ name: '', parent_id: null });
              }}>
                Cancel
              </Button>
              <Button onClick={addCategory} className="bg-primary" data-testid="save-category-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Category
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
